import {useCallback, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {useChekinSDK} from 'context/ChekinSDK';
import {SessionStorage} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {PREVIOUS_PAGE_STORAGE_KEY} from '../utils/constants';
import {useErrorToast} from '@chekin/shared';

const makeToString = (to?: string) => {
  if (!to) {
    return '';
  }

  return to.startsWith('/') ? to : `/${to}`;
};

function usePath() {
  const {mainToken} = useChekinSDK();
  const {pathname} = useLocation();
  const {id: routeGuestId} = useParams<{id: string}>();
  const mainPart = `/${mainToken}`;

  const getIVPath = useCallback(
    (to: string) => {
      const inIVFlow = pathname.split('/').includes(PATHS.verification.main);
      const guestSegment = inIVFlow && routeGuestId ? `/${routeGuestId}` : '';
      const base = `${PATHS.verification.main}${guestSegment}`;
      const ivRoute = to === PATHS.verification.main ? base : `${base}/${to}`;
      return `${mainPart}/${ivRoute}`;
    },
    [mainPart, pathname, routeGuestId],
  );

  const getTaxesPath = useCallback(
    (to: string) => {
      const ivRoute = to === PATHS.taxes.main ? to : `${PATHS.taxes.main}/${to}`;
      return `${mainPart}/${ivRoute}`;
    },
    [mainPart],
  );

  const getAuthPath = useCallback(
    (to: string) => {
      const authRoute = to === PATHS.auth.root ? to : `${PATHS.auth.root}/${to}`;
      return `${mainPart}/${authRoute}`;
    },
    [mainPart],
  );

  const getCheckoutPath = useCallback(
    (to: string) => {
      const authRoute = to === PATHS.checkout.main ? to : `${PATHS.checkout.main}/${to}`;
      return `${mainPart}/${authRoute}`;
    },
    [mainPart],
  );

  const getMainPath = useCallback(
    (to = '') => {
      if (to.includes('token')) {
        return `/${mainToken || ''}`;
      }
      const toString = makeToString(to);

      return `${mainPart}${toString}`;
    },
    [mainPart, mainToken],
  );

  const getPaymentsPath = useCallback(
    (to: string) => {
      const paymentsRoute =
        to === PATHS.payments.main ? to : `${PATHS.payments.main}/${to}`;
      return `${mainPart}/${paymentsRoute}`;
    },
    [mainPart],
  );

  const getSearchReservationPath = useCallback(
    (to: string) => {
      return `${mainPart}/${to}`;
    },
    [mainPart],
  );

  const getFullPath = useCallback(
    (to: string) => {
      const ivPathsArray = Object.values(PATHS.verification);
      const taxesPathsArray = Object.values(PATHS.taxes);
      const paymentsPathsArray = Object.values(PATHS.payments);
      const authPathsArray = Object.values(PATHS.auth);
      const checkoutPathsArray = Object.values(PATHS.checkout);
      const searchReservationPathsArray = Object.values(PATHS.searchReservation);
      const isPaymentsPath = paymentsPathsArray.includes(to);
      const isIVPath = ivPathsArray.includes(to);
      const isTaxesPath = taxesPathsArray.includes(to);
      const isAuthPath = authPathsArray.includes(to);
      const isCheckoutPath = checkoutPathsArray.includes(to);
      const isSearchReservationPath = searchReservationPathsArray.includes(to);
      if (isIVPath) {
        return getIVPath(to);
      } else if (isTaxesPath) {
        return getTaxesPath(to);
      } else if (isPaymentsPath) {
        return getPaymentsPath(to);
      } else if (isAuthPath) {
        return getAuthPath(to);
      } else if (isCheckoutPath) {
        return getCheckoutPath(to);
      } else if (isSearchReservationPath) {
        return getSearchReservationPath(to);
      } else {
        return getMainPath(to);
      }
    },
    [
      getIVPath,
      getMainPath,
      getTaxesPath,
      getPaymentsPath,
      getAuthPath,
      getCheckoutPath,
      getSearchReservationPath,
    ],
  );

  return {
    getIVPath,
    getMainPath,
    getFullPath,
    getAuthPath,
    getCheckoutPath,
    getSearchReservationPath,
  };
}

function useSetPreviousPage() {
  const location = useLocation();

  useEffect(() => {
    return () => {
      SessionStorage.set(PREVIOUS_PAGE_STORAGE_KEY, location.pathname);
    };
  }, [location.pathname]);
}

export {useErrorToast, usePath, useSetPreviousPage};
